/**
 * NEXUS UI - Gauge
 * Semi-circular gauge with needle indicator
 *
 * Arc spans 180 degrees:
 * - Min (0%) at 9 o'clock (left)
 * - Max (100%) at 3 o'clock (right)
 * - Arc curves through 12 o'clock (top)
 */

import React, { useMemo } from 'react';
import type { BaseProps, NexusVariant, NexusSize } from './types';

export interface GaugeProps extends BaseProps {
  value: number;
  min?: number;
  max?: number;
  variant?: NexusVariant;
  size?: NexusSize;
  label?: string;
  unit?: string;
  showValue?: boolean;
  showMinMax?: boolean;
  segments?: number;
  animated?: boolean;
  warningThreshold?: number;
  criticalThreshold?: number;
}

const variantColors: Record<NexusVariant, { base: string; glow: string }> = {
  cyan: { base: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { base: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { base: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { base: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { base: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { base: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

const sizeConfig: Record<NexusSize, { width: number; strokeWidth: number; fontSize: string }> = {
  sm: { width: 100, strokeWidth: 6, fontSize: '16px' },
  md: { width: 140, strokeWidth: 8, fontSize: '20px' },
  lg: { width: 180, strokeWidth: 10, fontSize: '26px' },
  xl: { width: 220, strokeWidth: 12, fontSize: '32px' },
};

export const Gauge: React.FC<GaugeProps> = ({
  value,
  min = 0,
  max = 100,
  variant = 'cyan',
  size = 'md',
  label,
  unit,
  showValue = true,
  showMinMax = true,
  segments = 0,
  animated = true,
  warningThreshold,
  criticalThreshold,
  className = '',
  style,
}) => {
  const config = sizeConfig[size];
  const colors = variantColors[variant];

  // Normalize value to 0-1 range
  const percentage = Math.min(Math.max((value - min) / (max - min), 0), 1);

  // Determine color based on thresholds
  const currentColor = useMemo(() => {
    if (criticalThreshold !== undefined && value >= criticalThreshold) {
      return variantColors.red;
    }
    if (warningThreshold !== undefined && value >= warningThreshold) {
      return variantColors.amber;
    }
    return colors;
  }, [value, warningThreshold, criticalThreshold, colors]);

  // SVG dimensions - height is half width plus padding for stroke and labels
  const width = config.width;
  const height = config.width / 2 + config.strokeWidth + 16;
  const radius = (config.width - config.strokeWidth * 2 - 8) / 2;

  // Center point - horizontally centered, vertically positioned so arc fits
  const cx = width / 2;
  const cy = height - 8;

  // Key positions on the arc
  // 9 o'clock (min): left side
  const minX = cx - radius;
  const minY = cy;
  // 3 o'clock (max): right side
  const maxX = cx + radius;
  const maxY = cy;

  // Background track: full 180° arc from 9 o'clock to 3 o'clock through 12 o'clock
  const trackPath = `M ${minX} ${minY} A ${radius} ${radius} 0 0 1 ${maxX} ${maxY}`;

  // Filled arc: from 9 o'clock to current value position
  const filledArcPath = useMemo(() => {
    if (percentage === 0) return '';

    // Calculate endpoint based on percentage
    // Angle goes from π (9 o'clock) to 0 (3 o'clock)
    const angle = Math.PI * (1 - percentage);
    const endX = cx + radius * Math.cos(angle);
    const endY = cy - radius * Math.sin(angle); // Subtract because SVG Y is inverted

    // Always use large-arc=0 since we never exceed 180°
    // sweep-flag=1 for clockwise direction (through the top)
    return `M ${minX} ${minY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`;
  }, [percentage, cx, cy, radius, minX, minY]);

  // Needle rotation: -90° at 0%, 0° at 50%, +90° at 100%
  const needleRotation = -90 + percentage * 180;

  // Segment tick marks
  const segmentTicks = useMemo(() => {
    if (segments <= 0) return [];

    return Array.from({ length: segments + 1 }).map((_, i) => {
      const tickPercentage = i / segments;
      const angle = Math.PI * (1 - tickPercentage);
      const innerR = radius - config.strokeWidth / 2 - 2;
      const outerR = radius + config.strokeWidth / 2 + 2;

      return {
        x1: cx + innerR * Math.cos(angle),
        y1: cy - innerR * Math.sin(angle),
        x2: cx + outerR * Math.cos(angle),
        y2: cy - outerR * Math.sin(angle),
      };
    });
  }, [segments, cx, cy, radius, config.strokeWidth]);

  const gradientId = `gauge-gradient-${Math.random().toString(36).slice(2, 9)}`;
  const glowId = `gauge-glow-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <div
      className={`nx-gauge ${className}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...style,
      }}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={currentColor.base} stopOpacity="0.4" />
            <stop offset="100%" stopColor={currentColor.base} stopOpacity="1" />
          </linearGradient>
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background track */}
        <path
          d={trackPath}
          fill="none"
          stroke="var(--nx-void-elevated)"
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
        />

        {/* Segment ticks */}
        {segmentTicks.map((tick, i) => (
          <line
            key={i}
            x1={tick.x1}
            y1={tick.y1}
            x2={tick.x2}
            y2={tick.y2}
            stroke="var(--nx-void-surface)"
            strokeWidth={2}
          />
        ))}

        {/* Filled arc (glow layer) */}
        {filledArcPath && (
          <path
            d={filledArcPath}
            fill="none"
            stroke={currentColor.base}
            strokeWidth={config.strokeWidth + 4}
            strokeLinecap="round"
            opacity={0.3}
            filter={`url(#${glowId})`}
            style={{
              transition: animated ? 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
            }}
          />
        )}

        {/* Filled arc */}
        {filledArcPath && (
          <path
            d={filledArcPath}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={config.strokeWidth}
            strokeLinecap="round"
            filter={`url(#${glowId})`}
            style={{
              transition: animated ? 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
            }}
          />
        )}

        {/* Needle */}
        <g
          style={{
            transformOrigin: `${cx}px ${cy}px`,
            transition: animated ? 'transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
            transform: `rotate(${needleRotation}deg)`,
          }}
        >
          <line
            x1={cx}
            y1={cy}
            x2={cx}
            y2={cy - radius + 6}
            stroke="var(--nx-text-primary)"
            strokeWidth={2}
            strokeLinecap="round"
          />
          <circle
            cx={cx}
            cy={cy}
            r={4}
            fill="var(--nx-void-surface)"
            stroke={currentColor.base}
            strokeWidth={2}
            filter={`url(#${glowId})`}
          />
        </g>

        {/* Min/Max labels */}
        {showMinMax && (
          <>
            <text
              x={minX}
              y={cy + 14}
              fill="var(--nx-text-ghost)"
              fontSize="9"
              fontFamily="var(--nx-font-mono)"
              textAnchor="middle"
            >
              {min}
            </text>
            <text
              x={maxX}
              y={cy + 14}
              fill="var(--nx-text-ghost)"
              fontSize="9"
              fontFamily="var(--nx-font-mono)"
              textAnchor="middle"
            >
              {max}
            </text>
          </>
        )}
      </svg>

      {/* Value display */}
      {showValue && (
        <div
          style={{
            fontFamily: 'var(--nx-font-display)',
            fontSize: config.fontSize,
            fontWeight: 700,
            color: 'var(--nx-text-primary)',
            marginTop: '4px',
            display: 'flex',
            alignItems: 'baseline',
            gap: '4px',
          }}
        >
          {value.toFixed(0)}
          {unit && (
            <span
              style={{
                fontFamily: 'var(--nx-font-mono)',
                fontSize: '12px',
                color: 'var(--nx-text-tertiary)',
              }}
            >
              {unit}
            </span>
          )}
        </div>
      )}

      {/* Label */}
      {label && (
        <div
          style={{
            fontFamily: 'var(--nx-font-interface)',
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--nx-text-tertiary)',
            marginTop: '2px',
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
};

export default Gauge;
